import { useMemo, type CSSProperties, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  tableExportColumnWidths,
  tableExportIndentKey,
  tableExportPeriod,
  tableExportTotals,
  tableExportWraps,
  type Branding,
  type CompanyProfile,
  type TableExportColumn,
  type TableExportRow,
  type TableExportSection,
  type TableExportSheet,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { closePrintView } from '../../lib/printclose';
import { date, time } from '../../lib/format';
import { readPrintTable } from '../../lib/tableExport';
import { Code, CodeText } from '../../components/Code';
import { Logo } from '../../components/Logo';
import { PrintSheet, useDocumentPaper } from '../../components/DocumentPaper';
import '../../styles/print.css';

interface Profile {
  tenant: { name: string };
  branding: Branding;
  company: CompanyProfile;
}

/** المالُ برقمين عشريّين دائمًا — الأعمدةُ تُقرأ متساويةَ الكسور، والسالبُ بين قوسين. */
const moneyFmt = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const numberFmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 });

function cellText(col: TableExportColumn, value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') return col.kind === 'date' ? date(value) : value;
  switch (col.kind) {
    case 'money':
      return value < 0 ? `(${moneyFmt.format(-value)})` : moneyFmt.format(value);
    case 'number':
      return numberFmt.format(value);
    case 'percent':
      return `${numberFmt.format(value)}٪`;
    default:
      return String(value);
  }
}

const isNumeric = (c: TableExportColumn) => c.kind === 'money' || c.kind === 'number' || c.kind === 'percent';

/**
 * محتوى الخليّة.
 *
 * الرمزُ («JV-2026-3169») معزولٌ لاتينيًّا لا يُكسر، أكان عمودَه كلَّه أم
 * مقطعًا في نصٍّ عربيّ («تحصيل PAY-2026-0137»). والنصُّ متعدّدُ الأسطر سطرٌ
 * رئيسٌ وتفاصيلُ تحته أصغرَ وأخفت — فلا يطول الصفُّ بتفاصيل البيان.
 */
function cellContent(col: TableExportColumn, value: string | number | null | undefined): ReactNode {
  const text = cellText(col, value);
  if (!text) return null;
  if (col.kind === 'code') return <Code>{text}</Code>;
  if (col.kind && col.kind !== 'text') return text;
  const [main, ...rest] = text.split('\n');
  return (
    <>
      <CodeText text={main} />
      {rest
        .filter((l) => l.trim())
        .map((l, i) => (
          <span key={i} className="cell-sub">
            <CodeText text={l} />
          </span>
        ))}
    </>
  );
}

/** صنفُ الخليّة: الرقمُ مصطفًّا والسالبُ أحمر، وما لا يلتفّ سطرٌ واحد. */
function cellClass(col: TableExportColumn, value: string | number | null | undefined): string | undefined {
  const cls = [
    isNumeric(col) && 'num',
    !tableExportWraps(col) && 'nowrap',
    typeof value === 'number' && value < 0 && col.kind === 'money' && 'neg',
  ].filter(Boolean);
  return cls.length ? cls.join(' ') : undefined;
}

/**
 * عرضُ العمود في PDF: الجدولُ يقيس محتواه بنفسه (`table-layout: auto`)، وهنا
 * التوجيه فقط — ما لا يلتفّ يضيق إلى أطول قيمةٍ فيه (`width: 1%` مع منع
 * الكسر)، فيذهب باقي العرض إلى أعمدة النصّ. و`minWidth` حدٌّ أدنى للنصّ
 * بالحروف (نحو ثلثَي «em» للحرف بخطّ الجدول).
 */
function colStyle(col: TableExportColumn): CSSProperties | undefined {
  if (!tableExportWraps(col)) return { width: '1%' };
  if (col.minWidth) return { width: `${(col.minWidth * 0.62).toFixed(1)}em` };
  return undefined;
}

/**
 * الصفحةُ عرضيّةٌ؟ ما طلبته الورقة، وإلّا فحين لا تتّسع الأعمدةُ الثابتة
 * (تاريخٌ ومبالغُ ورموز) في عرض A4 الطوليّ مع مكانٍ للنصّ، أو كثرت الأعمدة.
 */
function isWide(sheet: TableExportSheet): boolean {
  if (sheet.orientation) return sheet.orientation === 'landscape';
  return sheet.sections.some((s) => {
    if (s.columns.length > 8) return true;
    const widths = tableExportColumnWidths(s);
    const fixed = s.columns.reduce((sum, c, i) => sum + (tableExportWraps(c) ? 0 : widths[i]!), 0);
    return fixed > 80;
  });
}

const ROW_STYLE: Partial<Record<NonNullable<TableExportRow['_style']>, CSSProperties>> = {
  group: { fontWeight: 700 },
  heading: { fontWeight: 700 },
  subtotal: { fontWeight: 700, borderTop: '1pt solid currentColor' },
  total: { fontWeight: 700, borderTop: '1.5pt double currentColor' },
  note: { fontStyle: 'italic', color: '#6f7784' },
};

/**
 * جدولٌ معروضٌ مطبوعًا — ومنه «حفظ كـ PDF».
 *
 * المستندُ (TableExportDoc) يصل من الصفحة التي صدّرته عبر التخزين المحلّيّ
 * (lib/tableExport). والطريقُ إلى PDF هو المتصفّح لا مكتبةٌ في الصفحة:
 * المتصفّح يصل الحروفَ العربيّة ويرتّبها من اليمين كما على الشاشة. والترويسةُ
 * ترويسةُ مستندات الشركة، وكلُّ ورقةٍ في صفحتها — طوليّةً أو عرضيّةً بعدد
 * أعمدتها، بهوامش وترقيم صفحاتٍ من قاعدة الطباعة (print.css).
 */
export function PrintTable() {
  const [params] = useSearchParams();
  const doc = useMemo(() => readPrintTable(params.get('k')), [params]);
  const profile = useQuery({ queryKey: ['settings'], queryFn: () => api<Profile>('/settings') });
  /* ورقةُ الشركة: كشفُ الحساب على ورقته إن رُفعت، وكلُّ تصديرٍ آخر على العامّة. */
  const { paper, ready: paperReady } = useDocumentPaper(doc?.paper ?? 'letterhead');

  if (!doc) {
    return (
      <div className="print-page">
        <p>المستند غير متاح — أعد التصدير من الصفحة.</p>
      </div>
    );
  }
  if (profile.isError) {
    return (
      <div className="print-page">
        <p>تعذّر تحضير المستند.</p>
      </div>
    );
  }
  if (!profile.data || !paperReady) {
    return (
      <div className="print-page">
        <p>جارٍ التحضير…</p>
      </div>
    );
  }

  const { branding, company, tenant } = profile.data;
  const companyName = doc.company || company.legalName || tenant.name;
  const now = new Date().toISOString();
  const printedDay = date(now);
  const printedTime = time(now);

  return (
    <div
      className="print-root"
      style={{
        ['--doc-primary' as string]: branding.primary,
        ['--doc-ink' as string]: branding.ink,
      }}
    >
      <div className="print-toolbar no-print">
        <button className="btn btn-primary" onClick={() => window.print()}>
          طباعة أو حفظ PDF
        </button>
        <button className="btn" onClick={closePrintView}>
          إغلاق
        </button>
        <span className="small mute">اختر «حفظ كـ PDF» في نافذة الطباعة للحصول على ملف.</span>
      </div>

      {doc.sheets.map((sheet, i) => {
        const period = tableExportPeriod(sheet.period, date);
        return (
          <PrintSheet
            key={i}
            /* على ورقة الشركة: صفحاتُ A4 مقسومةٌ بالقياس؛ وبدونها هوامشُ الجدول وترقيمُ صفحاته. */
            paper={paper}
            className={`print-page table-doc${isWide(sheet) ? ' is-wide' : ''}`}
            style={i > 0 ? { breakBefore: 'page', marginTop: 'var(--sp-5)' } : undefined}
          >
            <header className="doc-header">
              <div className="doc-brand">
                {branding.logoUrl ? <img src={branding.logoUrl} alt="" className="doc-logo" /> : <Logo size={52} />}
                <div>
                  <div className="doc-company">{companyName}</div>
                  {branding.documentHeader && <div className="doc-tagline">{branding.documentHeader}</div>}
                </div>
              </div>
              <div className="doc-title-block">
                {doc.sheets.length > 1 && <div className="doc-kind">{doc.title}</div>}
                <div className="doc-number">{sheet.title}</div>
              </div>
            </header>

            <section className="doc-dates">
              {period && (
                <div className="doc-date-row">
                  <span className="doc-label">المدة</span>
                  <span>{period}</span>
                </div>
              )}
              {(sheet.details ?? []).map((d) => (
                <div key={d.label} className="doc-date-row">
                  <span className="doc-label">{d.label}</span>
                  <span>
                    <CodeText text={d.value} />
                  </span>
                </div>
              ))}
              <div className="doc-date-row">
                <span className="doc-label">طُبع في</span>
                {/* التاريخ ثم الساعة، كلٌّ في عزلته: «ص/م» العربية تقلب ترتيبهما لو اجتمعا. */}
                <span className="num">
                  <bdi>{printedDay}</bdi> — <bdi>{printedTime}</bdi>
                </span>
              </div>
            </section>

            {sheet.sections.map((section, j) => (
              <SectionTable key={j} section={section} />
            ))}

            {(sheet.notes ?? []).map((note) => (
              <p key={note} className="doc-table-note" data-keep-with-prev>
                <CodeText text={note} />
              </p>
            ))}
          </PrintSheet>
        );
      })}
    </div>
  );
}

function SectionTable({ section }: { section: TableExportSection }) {
  const totals = tableExportTotals(section);
  const indentKey = tableExportIndentKey(section);
  const align = (c: TableExportColumn): CSSProperties | undefined => (isNumeric(c) ? { textAlign: 'right' } : undefined);

  /*
   * صفُّ الإجمالي: نصُّه («الإجمالي والرصيد الختامي») يمتدّ على الأعمدة
   * الفارغة قبل أوّل مبلغ، فلا يلتفّ في عمود التاريخ الضيّق ولا يوسّعه.
   */
  const firstValue = totals
    ? section.columns.findIndex((c) => {
        const v = totals[c.key];
        return typeof v === 'number' || (v !== null && v !== undefined && v !== '' && !tableExportWraps(c) && c.kind !== 'date');
      })
    : -1;
  const lead = firstValue > 0 ? section.columns.slice(0, firstValue) : [];
  const leadTexts = totals ? lead.map((c) => totals[c.key]).filter((v) => v !== null && v !== undefined && v !== '') : [];
  const mergeLead = lead.length > 1 && leadTexts.length <= 1;

  return (
    <table className="doc-table data-table">
      <thead>
        {section.title && (
          <tr>
            <th colSpan={section.columns.length} className="doc-table-section">
              {section.title}
            </th>
          </tr>
        )}
        <tr>
          {section.columns.map((c) => (
            <th key={c.key} style={{ ...colStyle(c), ...align(c) }}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {section.rows.map((r, k) => (
          <tr key={k} className={r._style && r._style !== 'normal' ? `row-${r._style}` : undefined} style={r._style ? ROW_STYLE[r._style] : undefined}>
            {section.columns.map((c) => (
              <td
                key={c.key}
                className={cellClass(c, r[c.key])}
                style={{
                  ...align(c),
                  ...(c.key === indentKey && r._level ? { paddingInlineStart: `${2 + r._level * 4}mm` } : {}),
                }}
              >
                {cellContent(c, r[c.key])}
              </td>
            ))}
          </tr>
        ))}
        {section.rows.length === 0 && (
          <tr>
            <td colSpan={section.columns.length}>لا توجد بيانات في هذه المدة.</td>
          </tr>
        )}
      </tbody>
      {totals && (
        <tfoot>
          <tr>
            {mergeLead && <td colSpan={lead.length}>{leadTexts[0] ?? ''}</td>}
            {section.columns.slice(mergeLead ? lead.length : 0).map((c) => (
              <td key={c.key} className={cellClass(c, totals[c.key])} style={align(c)}>
                {cellContent(c, totals[c.key])}
              </td>
            ))}
          </tr>
        </tfoot>
      )}
    </table>
  );
}
